import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo, useRef, useCallback } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import styled from 'styled-components';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getIsReducedMotionEnabled } from '../../utils/getIsReducedMotionEnabled';
import { usePropertyDefinitionsByObjectTypeId } from '../../v2/hooks/usePropertyDefinition';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { SuccessIcon } from 'foundations-assets';
import { useIndicatorContentTransition } from '../hooks/useIndicatorContentTransition';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TRANSITION_DURATION = getIsReducedMotionEnabled() ? 0 : 300;
const FadingDiv = styled.div.withConfig({
  displayName: "AutosaveIndicator__FadingDiv",
  componentId: "sc-5k5sma-0"
})(["opacity:", ";transition:opacity ", "ms;"], props => props.$opacity, TRANSITION_DURATION);
const UndoButton = ({
  onClick,
  children
}) => {
  return /*#__PURE__*/_jsx(Button, {
    padding: "0",
    paddingInlineStart: "200",
    use: "transparent",
    onClick: onClick,
    children: children
  });
};
const IndicatorContent = ({
  status,
  changedPropertyLabels = [],
  onUndo = emptyFunction
}) => {
  const jsxElementOptions = {
    elements: {
      Button: UndoButton,
      // Awkwardly named to prevent breaking _jsx strings for non-english translations, while supporting old behavior
      UIFlex: Flex,
      UITruncateString: TruncateString
    },
    options: {
      buttonProps: {
        onClick: onUndo
      },
      flexProps: {
        alignItems: 'center'
      },
      truncateProps: {
        maxWidth: 250
      }
    }
  };
  if (status === 'pending') return /*#__PURE__*/_jsx(LoadingSpinner, {
    size: "xs",
    label: /*#__PURE__*/_jsx(Text, {
      as: "span",
      marginInlineStart: "100",
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.autosave.savingChanges"
      })
    }),
    showLabel: true,
    margin: "0"
  });
  if (status === 'success') return /*#__PURE__*/_jsx(Text, {
    as: "span",
    typography: "heading-50",
    children: changedPropertyLabels.length === 0 || changedPropertyLabels.length === 1 && changedPropertyLabels[0] === undefined ?
    /*#__PURE__*/
    // Special zero count case handling, since `zero` is no longer a
    // supported pluralized key option. This can happen if we have
    // changes, but the property label lookup failed for whatever reason
    _jsxs(Flex, {
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(IconHolder, {
        size: "600",
        color: "fill-positive-pressed",
        padding: "0",
        marginInlineEnd: "100",
        icon: SuccessIcon
      }), /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({
        message: "customerDataProperties.autosave.changesSavedNoLabel_jsx"
      }, jsxElementOptions))]
    }) : /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(IconHolder, {
        size: "600",
        color: "fill-positive-pressed",
        padding: "0",
        marginInlineEnd: "100",
        icon: SuccessIcon
      }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "customerDataProperties.autosave.changesSaved_jsx",
        elements: Object.assign({}, jsxElementOptions.elements),
        options: Object.assign({}, jsxElementOptions.options, {
          label: changedPropertyLabels[0],
          count: changedPropertyLabels.length
        })
      })]
    })
  });
  if (status === 'reverted') return /*#__PURE__*/_jsx(Text, {
    as: "span",
    typography: "heading-50",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.autosave.changesReverted"
    })
  });
  return null;
};
const getColors = status => {
  switch (status) {
    case 'success':
      return {
        backgroundColor: 'fill-positive-subtle',
        borderColor: 'fill-positive-pressed'
      };
    case 'pending':
    case 'reverted':
      return {
        backgroundColor: 'fill-tertiary-hover',
        borderColor: 'fill-tertiary-pressed'
      };
    default:
      return {
        backgroundColor: undefined,
        borderColor: undefined
      };
  }
};
const AutosaveIndicator = ({
  status,
  onUndo,
  packet
}) => {
  const measurementRef = useRef(null);
  const items = useMemo(() => {
    var _packet$items;
    return (_packet$items = packet === null || packet === void 0 ? void 0 : packet.items) !== null && _packet$items !== void 0 ? _packet$items : [];
  }, [packet]);
  const {
    data: itemPropertyDefintions
  } = usePropertyDefinitionsByObjectTypeId(items.map(item => item.request.objectTypeId));
  const changedPropertyLabels = useMemo(() => {
    if (!items.length || !itemPropertyDefintions) {
      return [];
    }
    return items.flatMap(item => {
      const {
        objectTypeId,
        properties
      } = item.request;
      return properties.map(property => {
        var _itemPropertyDefintio;
        return itemPropertyDefintions === null || itemPropertyDefintions === void 0 || (_itemPropertyDefintio = itemPropertyDefintions[objectTypeId]) === null || _itemPropertyDefintio === void 0 || (_itemPropertyDefintio = _itemPropertyDefintio[property.name]) === null || _itemPropertyDefintio === void 0 ? void 0 : _itemPropertyDefintio.property.label;
      }).filter(Boolean);
    });
  }, [items, itemPropertyDefintions]);
  const measureElement = useCallback(() => {
    if (!measurementRef.current) {
      return undefined;
    }
    return measurementRef.current.getBoundingClientRect().width || undefined;
  }, []);
  const shouldSkipTransition = useCallback((oldStatus, newStatus) => {
    return oldStatus === 'idle' || newStatus === 'idle';
  }, []);
  const {
    displayedValue: displayedStatus,
    opacity,
    targetWidth
  } = useIndicatorContentTransition({
    value: status,
    duration: TRANSITION_DURATION,
    shouldSkipTransition,
    measureElement
  });
  if (status === 'idle' && displayedStatus === 'idle') {
    return null;
  }
  const {
    backgroundColor,
    borderColor
  } = getColors(displayedStatus);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      ref: measurementRef,
      style: {
        position: 'fixed',
        left: '-999999px',
        visibility: 'hidden',
        pointerEvents: 'none'
      },
      children: /*#__PURE__*/_jsx(IndicatorContent, {
        status: status // Use incoming status for measurement
        ,
        changedPropertyLabels: changedPropertyLabels
      })
    }), /*#__PURE__*/_jsx(Flex, {
      blockSize: "48px",
      borderStyle: "solid",
      backgroundColor: backgroundColor,
      borderRadius: "100",
      borderWidth: "100",
      borderColor: borderColor,
      boxShadow: "100",
      paddingInline: "300",
      paddingBlock: "0",
      alignItems: "center",
      role: "status",
      children: /*#__PURE__*/_jsx("div", {
        style: {
          width: targetWidth,
          transition: `width ${TRANSITION_DURATION}ms`,
          whiteSpace: 'nowrap'
        },
        "data-test-id": "autosave-indicator",
        "data-selenium-test": "autosave-indicator",
        children: /*#__PURE__*/_jsx(FadingDiv, {
          $opacity: opacity,
          children: /*#__PURE__*/_jsx(IndicatorContent, {
            status: displayedStatus,
            changedPropertyLabels: changedPropertyLabels,
            onUndo: onUndo
          })
        })
      })
    })]
  });
};
export default AutosaveIndicator;